import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { Loading, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { MachineType } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { cn } from '@/utils/style'

import { SelectMachineWarning } from './select-machine-warning'

interface ImageSelectorProps {
  machine?: Machine
  type: MachineType
  value?: Image
  count?: number
  onChange: (value?: Image) => void
  onImagesChange?: (value: Image[]) => void
}

export function ImageSelector({
  value,
  machine,
  type,
  count,
  onChange,
  onImagesChange,
}: ImageSelectorProps) {
  const [loading, setLoading] = useState(false)
  const [images, setImages] = useState<Image[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 99

  const getImages = useCallback(
    async (p = page) => {
      setLoading(true)
      if (!machine) return

      console.log('machine', machine)

      const res = await API.getImageList({
        agent_id: machine.agent_id ?? 0,
        machine_category: machine.machineCategory,
        resource_desc: JSON.stringify({
          agentDomains: machine.domains,
          attributeTags: 1,
          unitQty: count,
          machineClassTag: machine.unitSpec?.classTag,
          gpuName: machine.unitSpec?.gpuName,
          cpuName: machine.unitSpec?.cpuName,
        }),
        page: p,
        per_page: pageSize,
      })

      if (res.code === 0) {
        setPage(res.pagination.page)
        // setImages(res.images.map((im) => new Image(im)))
        setImages(
          res.images
            .filter(
              (im) =>
                machine.unitSpec?.gpuName?.includes('H100') ||
                (im.resID !== 'img-5yp6v8l8' && im.resID !== 'img-y4lvnxqw'),
            )
            .map((im) => new Image(im)),
        )
        onImagesChange?.(res.images.map((im) => new Image(im)))
      }

      setLoading(false)
    },

    [page, machine, type],
  )

  useEffect(() => {
    if (machine) {
      getImages(1)
      onChange(undefined)
    }
  }, [machine])

  if (!machine) {
    return <SelectMachineWarning />
  }

  return (
    <div className="bg-white rounded-2xl">
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && images.length > 0 && (
        <div className="grid grid-cols-4 gap-4 max-h-[300px] overflow-auto pr-6">
          {images.map((image) => (
            <div
              key={image.id}
              className={cn('p-4 border border-[#E1E4EA] rounded-lg cursor-pointer', {
                'bg-[#F1FCF8] border-[#2bcaa5]': value?.id === image.id,
              })}
              onClick={() => onChange(image)}
            >
              <div className="t-xs mb-3 text-primary">{image.resID}</div>
              <div className="t-sb mb-0.5 text-primary">{image.alias}</div>
              <p className="text-secondary t-xs">{image.description}</p>
            </div>
          ))}
        </div>
      )}

      {!loading && images.length === 0 && (
        <EmptyView
          content={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
            </span>
          }
        />
      )}
    </div>
  )
}
